/**
 * CommissionInlineEditor - Editor inline para regras de comissão
 */
import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Upload, DollarSign, Trash2, CheckSquare, XSquare } from "lucide-react";

export function CommissionInlineEditor({
  tabela,
  comissoesEditando,
  onAbrirImportacao,
  onAdicionarLinha,
  onSalvar,
  onAtualizarComissao,
  onExcluirLinha,
  onExcluirMultiplas
}) {
  const [selecionadas, setSelecionadas] = useState(new Set());

  // Verificar se todas estão selecionadas
  const todasSelecionadas = useMemo(() => {
    return comissoesEditando.length > 0 && selecionadas.size === comissoesEditando.length;
  }, [comissoesEditando, selecionadas]);

  // Verificar se alguma está selecionada
  const algumaSelecionada = selecionadas.size > 0;

  // Toggle seleção de uma linha
  const toggleSelecionada = (id) => {
    setSelecionadas(prev => {
      const novoSet = new Set(prev);
      if (novoSet.has(id)) {
        novoSet.delete(id);
      } else {
        novoSet.add(id);
      }
      return novoSet;
    });
  };

  // Selecionar/Deselecionar todas
  const toggleTodasSelecionadas = () => {
    if (todasSelecionadas) {
      setSelecionadas(new Set());
    } else {
      setSelecionadas(new Set(comissoesEditando.map(c => c.id)));
    }
  };

  // Excluir selecionadas
  const handleExcluirSelecionadas = () => {
    if (selecionadas.size === 0) return;
    
    // Se há função de excluir múltiplas, usar ela
    if (onExcluirMultiplas) {
      onExcluirMultiplas(Array.from(selecionadas));
    } else {
      // Senão, excluir uma por uma
      selecionadas.forEach(id => onExcluirLinha(id));
    }
    setSelecionadas(new Set());
  };

  // Limpar seleção quando tabela muda
  const limparSelecao = () => {
    setSelecionadas(new Set());
  };

  return (
    <div className="p-4 bg-slate-50 border-t">
      {/* Barra de Ações */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-emerald-800">
          Tabela de Comissões - Configure os percentuais
        </h4>
        <div className="flex gap-2">
          {/* Ações de seleção múltipla */}
          {algumaSelecionada && (
            <Button 
              size="sm"
              variant="destructive"
              className="gap-1"
              onClick={handleExcluirSelecionadas}
              data-testid="btn-excluir-selecionadas"
            >
              <Trash2 className="h-3 w-3" />
              Excluir ({selecionadas.size})
            </Button>
          )}
          <Button 
            size="sm"
            variant="outline"
            className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50"
            onClick={() => onAbrirImportacao(tabela)}
            data-testid="btn-importar-comissoes"
          >
            <Upload className="h-3 w-3" />
            Importar
          </Button>
          <Button 
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => onAdicionarLinha(tabela)}
            data-testid="btn-adicionar-linha"
          >
            <Plus className="h-3 w-3" />
            Linha
          </Button>
          <Button 
            size="sm"
            className="gap-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onSalvar(tabela.id)}
            data-testid="btn-salvar-comissoes"
          >
            <DollarSign className="h-3 w-3" />
            Salvar
          </Button>
        </div>
      </div>
      
      {/* Tabela Editável */}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-emerald-100">
              <TableHead className="w-[40px] text-center">
                <Checkbox
                  checked={todasSelecionadas}
                  onCheckedChange={toggleTodasSelecionadas}
                  className="data-[state=checked]:bg-emerald-600"
                  data-testid="checkbox-selecionar-todas"
                />
              </TableHead>
              <TableHead className="font-bold text-sm min-w-[280px]">Produto/Nome</TableHead>
              <TableHead className="text-center font-bold text-sm w-[70px]">Prazo</TableHead>
              <TableHead className="text-center font-bold text-sm w-[90px]">Fator</TableHead>
              <TableHead className="text-center font-bold text-sm w-[70px]">Taxa %</TableHead>
              <TableHead className="text-center font-bold text-sm w-[120px]">Tipo</TableHead>
              <TableHead className="text-center font-bold text-sm bg-indigo-100 w-[110px]">Tipo Comissão</TableHead>
              <TableHead className="text-center font-bold text-sm bg-slate-200 w-[110px]">Vl.Ini R$</TableHead>
              <TableHead className="text-center font-bold text-sm bg-slate-200 w-[110px]">Vl.Fim R$</TableHead>
              <TableHead className="text-center font-bold text-sm bg-amber-100 w-[100px]">Mod. Cálc.</TableHead>
              <TableHead className="text-center font-bold text-sm bg-blue-100 w-[60px]">Emp %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-green-100 w-[60px]">Tele %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-yellow-100 w-[60px]">Corr %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-orange-100 w-[60px]">Aten %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-purple-100 w-[60px]">G.Ad %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-pink-100 w-[60px]">G.Op %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-teal-100 w-[60px]">Bônus %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-cyan-100 w-[60px]">Dif. %</TableHead>
              <TableHead className="text-center font-bold text-sm bg-lime-100 w-[130px]">Início Vig.</TableHead>
              <TableHead className="text-center font-bold text-sm bg-rose-100 w-[130px]">Fim Vig.</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comissoesEditando.length === 0 ? (
              <TableRow>
                <TableCell colSpan={21} className="text-center py-6 text-slate-400 text-sm">
                  Clique em &quot;+ Linha&quot; para adicionar comissões
                </TableCell>
              </TableRow>
            ) : (
              comissoesEditando.map((com) => (
                <CommissionRow
                  key={com.id}
                  comissao={com}
                  tipoOperacao={tabela.tipo_operacao}
                  selecionada={selecionadas.has(com.id)}
                  onToggleSelecionada={() => toggleSelecionada(com.id)}
                  onAtualizar={onAtualizarComissao}
                  onExcluir={onExcluirLinha}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Rodapé com info de seleção */}
      {comissoesEditando.length > 0 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>
            {selecionadas.size > 0 
              ? `${selecionadas.size} de ${comissoesEditando.length} linha(s) selecionada(s)`
              : `${comissoesEditando.length} linha(s) total`
            }
          </span>
          {algumaSelecionada && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6 text-slate-500 hover:text-slate-700"
              onClick={limparSelecao}
            >
              <XSquare className="h-3 w-3 mr-1" />
              Limpar seleção
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Componente separado para cada linha de comissão
function CommissionRow({ comissao, tipoOperacao, selecionada, onToggleSelecionada, onAtualizar, onExcluir }) {
  const com = comissao;

  const handleNumericChange = (campo, value) => {
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      onAtualizar(com.id, campo, value === '' ? (campo === 'fator' || campo === 'taxa' ? null : 0) : value);
    }
  };

  const handleDecimalChange = (campo, value) => {
    if (value === '' || /^[0-9]*[,.]?[0-9]*$/.test(value)) {
      onAtualizar(com.id, campo, value);
    }
  };

  return (
    <TableRow className={`hover:bg-emerald-50 ${selecionada ? 'bg-blue-50' : ''}`}>
      <TableCell className="p-1 text-center">
        <Checkbox
          checked={selecionada}
          onCheckedChange={onToggleSelecionada}
          className="data-[state=checked]:bg-blue-600"
          data-testid={`checkbox-linha-${com.id}`}
        />
      </TableCell>
      <TableCell className="p-2">
        <Input
          className="h-9 text-sm w-full"
          placeholder="Nome do Produto"
          value={com.produto || ""}
          onChange={(e) => onAtualizar(com.id, 'produto', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1">
        <Input
          className="h-9 text-sm text-center w-[60px]"
          placeholder="120"
          value={com.prazo || ""}
          onChange={(e) => onAtualizar(com.id, 'prazo', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[80px]"
          placeholder="0.0555"
          value={com.fator || ""}
          onChange={(e) => handleNumericChange('fator', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[60px]"
          placeholder="1.85"
          value={com.taxa || ""}
          onChange={(e) => handleNumericChange('taxa', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 text-center">
        <span className="text-sm text-slate-600 font-medium">
          {com.tipo || tipoOperacao || "-"}
        </span>
      </TableCell>
      <TableCell className="p-1 bg-indigo-50">
        <Select 
          value={com.tipoComissao || "Valor Líquido"} 
          onValueChange={(v) => onAtualizar(com.id, 'tipoComissao', v)}
        >
          <SelectTrigger className="h-9 text-xs w-[100px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Valor Líquido">Valor Líquido</SelectItem>
            <SelectItem value="Valor Bruto">Valor Bruto</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="p-1 bg-slate-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[100px]"
          placeholder="500,00"
          value={com.valorInicial || ""}
          onChange={(e) => handleDecimalChange('valorInicial', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-slate-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[100px]"
          placeholder="5000,00"
          value={com.valorFinal || ""}
          onChange={(e) => handleDecimalChange('valorFinal', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-amber-50">
        <Select 
          value={com.modeloCalculo || "VALOR_CONTRATO"} 
          onValueChange={(v) => onAtualizar(com.id, 'modeloCalculo', v)}
        >
          <SelectTrigger className="h-9 text-xs w-[90px]">
            <SelectValue placeholder="Modelo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="VALOR_CONTRATO">V.Líquido</SelectItem>
            <SelectItem value="COMISSAO_EMPRESA">Com.Emp</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="p-1 bg-blue-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-blue-50"
          placeholder="0"
          value={com.empresa || ""}
          onChange={(e) => handleNumericChange('empresa', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-green-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-green-50"
          placeholder="0"
          value={com.teleVenda || ""}
          onChange={(e) => handleNumericChange('teleVenda', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-yellow-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-yellow-50"
          placeholder="0"
          value={com.corretor || ""}
          onChange={(e) => handleNumericChange('corretor', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-orange-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-orange-50"
          placeholder="0"
          value={com.atendente || ""}
          onChange={(e) => handleNumericChange('atendente', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-purple-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-purple-50"
          placeholder="0"
          value={com.gerAdm || ""}
          onChange={(e) => handleNumericChange('gerAdm', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-pink-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-pink-50"
          placeholder="0"
          value={com.gerOper || ""}
          onChange={(e) => handleNumericChange('gerOper', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-teal-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-teal-50"
          placeholder="0"
          value={com.bonus || ""}
          onChange={(e) => handleNumericChange('bonus', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-cyan-50">
        <Input
          type="text"
          className="h-9 text-sm text-center w-[50px] bg-cyan-50"
          placeholder="0"
          value={com.diferido || ""}
          onChange={(e) => handleNumericChange('diferido', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-lime-50">
        <Input
          type="date"
          className="h-9 text-sm text-center w-[120px] bg-lime-50"
          value={com.dataInicioVigencia || ""}
          onChange={(e) => onAtualizar(com.id, 'dataInicioVigencia', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 bg-rose-50">
        <Input
          type="date"
          className="h-9 text-sm text-center w-[120px] bg-rose-50"
          value={com.dataFimVigencia || ""}
          onChange={(e) => onAtualizar(com.id, 'dataFimVigencia', e.target.value)}
        />
      </TableCell>
      <TableCell className="p-1 text-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 hover:bg-red-50"
          onClick={() => onExcluir(com.id)}
          data-testid={`btn-excluir-linha-${com.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
